import type { FragmentType } from 'src/gql';
import type { Deprecated_HandleItemUpdate } from 'src/types/account';
import { DialogTitle } from '@savvy-kit/components/dialog';
import { cn } from '@savvy-kit/lib/utils';
import ItemHeader from 'src/components/portfolio/views/item-header';
import TrackedDialog from 'src/components/tracked/tracked-dialog';
import { graphql, unmaskFragment } from 'src/gql';

import type { AssetClassName } from '@savvy/constants/generated/gql-enums';
import type { ID } from '@savvy/utils/id';
import { useIsSmallScreen } from '@savvy/deprecated-shared/hooks/use-is-small-screen';

import AddEditCompanyEquity from '../company-equity/add-edit-company-equity';
import AddEditRealEstate from './add-edit-real-estate';
import AssetForm from './asset-form';

export const EditAssetDialogFragment = graphql(/* GraphQL */ `
  fragment EditAssetDialog on Asset {
    ...AddEditRealEstate
    ...AddEditCompanyEquity
    ...AssetForm
    assetClass
  }
`);

type EditAssetDialogProps = {
  readonly assetFrag: FragmentType<typeof EditAssetDialogFragment>;
  readonly onBack: () => void;
  readonly onClose: () => void;
  readonly onEdit: Deprecated_HandleItemUpdate;
  readonly clientUserId?: ID;
};

export function EditAssetDialog({ assetFrag, onBack, onClose, onEdit, clientUserId }: EditAssetDialogProps) {
  const asset = unmaskFragment(EditAssetDialogFragment, assetFrag);
  const thisShouldReallyBeASwitchCaseBlock: AssetClassName[] = ['real_estate', 'company_equity'];
  const isSmallScreen = useIsSmallScreen();
  return (
    <TrackedDialog
      elementName="edit-asset-dialog"
      open
      onOpenChange={(nextOpen) => !nextOpen && onClose()}
      size={isSmallScreen ? 'fullscreen' : undefined}
      className={cn('flex flex-col p-0', !isSmallScreen && 'max-h-[85vh]')}
    >
      <DialogTitle className="sr-only">Edit asset</DialogTitle>
      {/* pt-20 clears the content's absolute ItemDialogBackButton (top-6 + h-10). */}
      <div className="min-h-0 flex-1 overflow-y-auto px-6 pb-6 pt-20">
        {asset.assetClass === 'real_estate' && (
          <AddEditRealEstate clientUserId={clientUserId} assetFrag={asset} onClickBack={onBack} onSuccess={onEdit} />
        )}
        {asset.assetClass === 'company_equity' && (
          <AddEditCompanyEquity assetFrag={asset} onClickBack={onBack} onSuccess={onEdit} />
        )}
        {!thisShouldReallyBeASwitchCaseBlock.includes(asset.assetClass) && (
          <>
            <ItemHeader text="Asset details" />
            <AssetForm onClickBack={onBack} assetFrag={asset} onSuccess={onEdit} />
          </>
        )}
      </div>
    </TrackedDialog>
  );
}
